// External Imports
import {
  BodyText,
  Layout,
  Stack,
  Title,
  css,
  pxToRem,
  styled,
} from '@lessonnine/design-system.lib';
import { h } from 'preact';
import tokens from '@lessonnine/design-tokens.lib/dist/json/variables.json';

// Internal Imports
import { useStore } from '../../state/context';
import { Carousel } from '../Carousel/Carousel';
import { pxToRemParse } from '../../utils/styles';
import alexanderImage from '../../assets/images/teachers/alexander.webp';
import anissaImage from '../../assets/images/teachers/anissa.webp';
import fredericoImage from '../../assets/images/teachers/frederico.webp';
import mortarboardIcon from '../../assets/images/icons/mortarboard-icon.svg';
import priscillaImage from '../../assets/images/teachers/priscilla.webp';
import rubyImage from '../../assets/images/teachers/ruby.webp';
import tomatoesDesktopImage from '../../assets/images/tomatoes-desktop.webp';
import trophyIcon from '../../assets/images/icons/trophy-icon.svg';
import { LayoutWithSiteMaxWidth } from '../LayoutWithSiteMaxWidth/LayoutWithSiteMaxWidth';

// Local Variables
const desktopBreakpoint = parseInt(tokens.viewports.breakpoints.medium, 10);
const BrandColorText = styled.span`
  color: ${({ theme }) => theme.color.brand.default};
  font-style: italic;
`;

const TomatoesDesktopImage = styled.img`
  display: none;
  @media (min-width: ${pxToRem(desktopBreakpoint)}) {
    max-height: ${pxToRem(532)};
    border-radius: ${tokens.size.radius.large};
    display: block;
  }
`;

const CarouselContainer = styled(Stack)`
  overflow: hidden;
`;

const listStyles = css`
  padding-left: ${pxToRemParse(tokens.space.x8)};

  & > li {
    margin-bottom: ${pxToRemParse(tokens.space.x4)};
  }

  @media (min-width: ${tokens.viewports.breakpoints.small}) {
    & > li {
      margin-bottom: ${pxToRemParse(tokens.space.x2)};
    }
  }
`;
const EducationList = styled.ul`
  list-style-image: url('${mortarboardIcon}');
  ${listStyles}
`;

const QualificationsList = styled.ul`
  list-style-image: url('${trophyIcon}');
  ${listStyles}

  & > li:last-of-type {
    margin-bottom: 0;
  }
`;

const TeacherImage = styled.img`
  height: ${pxToRem(75)};

  @media (min-width: ${tokens.viewports.breakpoints.small}) {
    height: ${pxToRem(125)};
  }
`;

const TeacherQuote = styled(BodyText)`
  @media (min-width: ${tokens.viewports.breakpoints.small}) {
    order: 1;
  }
`;

// Component Definition
function TeachersSection() {
  const { t } = useStore();
  const carouselSlideData = [
    {
      education: [t.teachers.card1.education1.text, t.teachers.card1.education2.text],
      heading: t.teachers.card1.name,
      image: fredericoImage,
      qualifications: [t.teachers.card1.qualification1.text],
      quote: t.teachers.card1.bio,
    },
    {
      education: [t.teachers.card2.education1.text],
      heading: t.teachers.card2.name,
      image: priscillaImage,
      qualifications: [t.teachers.card2.qualification1.text],
      quote: t.teachers.card2.bio,
    },
    {
      education: [],
      heading: t.teachers.card3.name,
      image: anissaImage,
      qualifications: [t.teachers.card3.qualification1.text],
      quote: t.teachers.card3.bio,
    },
    {
      education: [t.teachers.card4.education1.text],
      heading: t.teachers.card4.name,
      image: alexanderImage,
      qualifications: [t.teachers.card4.qualification1.text],
      quote: t.teachers.card4.bio,
    },
    {
      education: [t.teachers.card5.education1.text],
      heading: t.teachers.card5.name,
      image: rubyImage,
      qualifications: [],
      quote: t.teachers.card5.bio,
    },
  ];
  return (
    <Stack
      alignItems="center"
      gap={48}
      padding={{
        medium: [0, pxToRem(154), pxToRem(80), pxToRem(154)],
        small: [pxToRem(48), 0],
      }}
    >
      <LayoutWithSiteMaxWidth
        direction="stack"
        gap={{ medium: pxToRemParse(tokens.space.x24), small: pxToRemParse(tokens.space.x8) }}
      >
        <Title as="h2" size={{ medium: 'large', small: 'small' }} textAlign="center">
          {t.teachers.headline.part1} <BrandColorText>{t.teachers.headline.part2}</BrandColorText>{' '}
          {t.teachers.headline.part3}
        </Title>
        <Layout
          direction={{ medium: 'inline', small: 'stack' }}
          gap={{ medium: pxToRemParse(tokens.space.x24), small: pxToRemParse(tokens.space.x8) }}
        >
          <TomatoesDesktopImage alt="tomatoes" src={tomatoesDesktopImage} />
          <CarouselContainer align="center">
            <Carousel
              controlsPosition={{ medium: 'bottomLeft', small: 'bottomCenter' }}
              slidesToShow={1}
            >
              {carouselSlideData.map(({ education, heading, image, qualifications, quote }) => (
                <Stack
                  alignItems={{ medium: 'leading', small: 'center' }}
                  gap={{ medium: pxToRem(32), small: pxToRem(16) }}
                  padding={{
                    medium: [0, 0, pxToRem(28), 0],
                    small: [0, pxToRem(16)],
                  }}
                >
                  <TeacherImage src={image} />
                  <Title as="span" size={{ medium: 'small', small: 'xsmall' }}>
                    {heading}
                  </Title>
                  <TeacherQuote textAlign={{ medium: 'left', small: 'center' }}>
                    {quote}
                  </TeacherQuote>
                  <div>
                    {education.length > 0 && (
                      <EducationList>
                        {education.map((text) => (
                          <li>
                            <BodyText size="small">{text}</BodyText>
                          </li>
                        ))}
                      </EducationList>
                    )}
                    {qualifications.length > 0 && (
                      <QualificationsList>
                        {qualifications.map((text) => (
                          <li>
                            <BodyText size="small">{text}</BodyText>
                          </li>
                        ))}
                      </QualificationsList>
                    )}
                  </div>
                </Stack>
              ))}
            </Carousel>
          </CarouselContainer>
        </Layout>
      </LayoutWithSiteMaxWidth>
    </Stack>
  );
}

// Module Exports
export { TeachersSection };
